import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { getNavGuard, subscribeNavGuard, setNavGuard } from '@/lib/navGuard';

/**
 * App-wide navigation guard. Mounted once inside the authenticated Layout.
 * When a page has registered unsaved work (via useUnsavedChanges), this
 * intercepts:
 *   - clicks on in-app navigation links (sidebar / menu items)
 *   - the browser Back/Forward buttons (via a pinned history sentinel)
 * and shows a confirmation so the user can cancel and keep their work.
 * (Tab close / refresh is handled by the per-page beforeunload listener.)
 */
export function NavigationGuard() {
  const navigate = useNavigate();
  const [dirty, setDirty] = useState(getNavGuard().dirty);
  const [pending, setPending] = useState(null); // { type:'link'|'back', href?, message }
  const bypassRef = useRef(false);

  useEffect(() => subscribeNavGuard(() => setDirty(getNavGuard().dirty)), []);

  // Intercept clicks on internal navigation links (sidebar / menu items).
  useEffect(() => {
    const onClick = (e) => {
      if (!getNavGuard().dirty) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      let el = e.target;
      while (el && el !== document.body) {
        if (el.tagName === 'A') {
          const href = el.getAttribute('href');
          const target = el.getAttribute('target');
          if (href && href.startsWith('/') && !target) {
            const current = window.location.pathname + window.location.search;
            if (href !== current) {
              e.preventDefault();
              e.stopPropagation();
              setPending({ type: 'link', href, message: getNavGuard().message });
            }
          }
          return;
        }
        el = el.parentElement;
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Intercept the browser Back / Forward buttons while dirty.
  useEffect(() => {
    if (!dirty) return;
    // Pin a sentinel entry so the first Back press is absorbed here.
    window.history.pushState({ __navGuard: true }, '', window.location.href);
    const onPop = () => {
      if (bypassRef.current) { bypassRef.current = false; return; }
      if (!getNavGuard().dirty) return;
      setPending({ type: 'back', message: getNavGuard().message });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [dirty]);

  const cancel = useCallback(() => {
    if (pending?.type === 'back') {
      // Re-pin to keep the user on the current page.
      window.history.pushState({ __navGuard: true }, '', window.location.href);
    }
    setPending(null);
  }, [pending]);

  const proceed = useCallback(() => {
    const p = pending;
    setPending(null);
    setNavGuard(false);
    if (!p) return;
    if (p.type === 'link') {
      navigate(p.href);
    } else if (p.type === 'back') {
      bypassRef.current = true;
      window.history.back();
    }
  }, [pending, navigate]);

  return (
    <Dialog open={!!pending} onOpenChange={(v) => { if (!v) cancel(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm" data-testid="nav-guard-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Leave this page?
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-sm text-muted-foreground mt-2">{pending?.message || 'You may lose unsaved work if you continue.'}</DialogDescription>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={cancel} className="flex-1 rounded-lg" data-testid="nav-guard-cancel">Stay on Page</Button>
          <Button variant="destructive" onClick={proceed} className="flex-1 rounded-lg" data-testid="nav-guard-leave">Leave Anyway</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
